import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import {
    User,
    Mail,
    Phone,
    CreditCard,
    Building2,
    MapPin,
    X,
    Save,
    AlertTriangle,
    CheckCircle,
    Eye,
    EyeOff
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { normalizeSSN, formatSSNForDisplay, getSSNValidationError } from '../../../../utils/ssnUtils';
import { validateClearingNumber, validateAccountNumber, getBankInfo } from '../../../../utils/bankAccountUtils';

// Helper functions for field mapping
const mapEmployeeFromApi = (apiEmployee) => {
    if (!apiEmployee) return null;

    console.log('EmployeeEditModal: Raw API employee data:', apiEmployee);

    // Handle name splitting for EmployeeList data format
    let firstname = apiEmployee.firstname || '';
    let lastname = apiEmployee.lastname || '';

    // If firstname/lastname are missing but name exists (from EmployeeList API)
    if ((!firstname || !lastname) && apiEmployee.name) {
        const nameParts = apiEmployee.name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
            firstname = nameParts[0];
            lastname = nameParts.slice(1).join(' '); // Handle multiple last names
        } else if (nameParts.length === 1) {
            firstname = nameParts[0];
            lastname = ''; // Will need to be filled by user
        }
        console.log('EmployeeEditModal: Split name into:', { firstname, lastname, originalName: apiEmployee.name });
    }

    const mappedEmployee = {
        ...apiEmployee,
        // Handle name fields
        firstname,
        lastname,
        // Map backend field names to frontend field names
        // Backend uses: clearingnumber, accountnumber, postnumber, city
        // Frontend uses: clearingnumber, accountnumber, postnummer, ort
        clearingnumber: apiEmployee.clearingnumber || apiEmployee.clearingnbr || '',
        accountnumber: apiEmployee.accountnumber || apiEmployee.accountnbr || '',
        postnummer: apiEmployee.postnumber || apiEmployee.postnummer || '',
        ort: apiEmployee.city || apiEmployee.ort || '',
        // Ensure optional fields have default values
        jobtitle: apiEmployee.jobtitle || '',
        phonenumber: apiEmployee.phonenumber || '',
        address: apiEmployee.address || '',
        email: apiEmployee.email || '',
        ssn: apiEmployee.ssn || '',
        // Status handling - only default to true if we have actual employee data
        active: Object.keys(apiEmployee).length > 1 ?
            (apiEmployee.status === 'active' || apiEmployee.active === true) :
            false
    };

    console.log('EmployeeEditModal: Mapped employee data:', mappedEmployee);
    return mappedEmployee;
};

const mapEmployeeToApi = (formData) => {
    return {
        ...formData,
        // Map frontend field names to backend field names
        clearingnbr: formData.clearingnumber,
        accountnbr: formData.accountnumber,
        postnumber: formData.postnummer,
        city: formData.ort,
        // Normalize SSN for backend (10 digits without dash)
        ssn: normalizeSSN(formData.ssn),
        // Map active boolean to status enum
        status: formData.active ? 'active' : 'inactive',
        // Remove frontend-specific fields
        clearingnumber: undefined,
        accountnumber: undefined,
        postnummer: undefined,
        ort: undefined,
        active: undefined
    };
};

// InputField component extracted to prevent re-creation on every render
const InputField = React.memo(({
    icon: Icon,
    label,
    name,
    type = 'text',
    required = false,
    placeholder = '',
    description = null,
    showToggle = false,
    value,
    onChange,
    error,
    showValidation,
    showSSN,
    onToggleSSN
}) => (
    <div className="input-group">
        <label className="input-label">
            {label}
            {required && <span className="required-indicator">*</span>}
        </label>
        {description && (
            <p className="input-description">{description}</p>
        )}
        <div className="input-container">
            <div className="input-icon">
                <Icon size={16} />
            </div>
            <Form.Control
                type={showToggle && name === 'ssn' ? (showSSN ? 'text' : 'password') : type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className={`modern-input ${error ? 'error' : ''}`}
                required={required}
            />
            {showToggle && name === 'ssn' && (
                <button
                    type="button"
                    className="input-toggle"
                    onClick={onToggleSSN}
                >
                    {showSSN ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
            )}
        </div>
        {showValidation && error && (
            <div className="error-message">
                <AlertTriangle size={14} />
                <span>{error}</span>
            </div>
        )}
    </div>
));

InputField.displayName = 'InputField';

const EmployeeEditModal = ({ show, handleClose, employee, companyId, onSave }) => {
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        ssn: '',
        email: '',
        phonenumber: '',
        jobtitle: '',
        clearingnumber: '',
        accountnumber: '',
        address: '',
        postnummer: '',
        ort: '',
        active: true
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [showSSN, setShowSSN] = useState(true);
    const [identifiedBank, setIdentifiedBank] = useState(null);

    // Initialize form data when modal is shown or employee changes
    useEffect(() => {
        const fetchFullEmployeeData = async () => {
            if (show && employee && employee.employee_id) {
                try {
                    console.log('EmployeeEditModal: Fetching full employee data for:', employee.employee_id);

                    // Fetch full employee details from API
                    const response = await axios.get(
                        getApiUrl(`${companyId}/hr/employees/${employee.employee_id}`),
                        { withCredentials: true }
                    );

                    const fullEmployee = response.data.data || response.data;
                    console.log('EmployeeEditModal: Full employee data:', fullEmployee);

                    const mappedEmployee = mapEmployeeFromApi(fullEmployee);
                    console.log('EmployeeEditModal: Mapped employee data:', mappedEmployee);

                    setFormData({
                        firstname: mappedEmployee.firstname || '',
                        lastname: mappedEmployee.lastname || '',
                        ssn: mappedEmployee.ssn || '',
                        email: mappedEmployee.email || '',
                        phonenumber: mappedEmployee.phonenumber || '',
                        jobtitle: mappedEmployee.jobtitle || '',
                        clearingnumber: mappedEmployee.clearingnumber || '',
                        accountnumber: mappedEmployee.accountnumber || '',
                        address: mappedEmployee.address || '',
                        postnummer: mappedEmployee.postnummer || '',
                        ort: mappedEmployee.ort || '',
                        active: mappedEmployee.active !== undefined ? mappedEmployee.active : true
                    });
                    setErrors({});
                    setShowValidation(false);
                } catch (error) {
                    console.error('Error fetching full employee data:', error);
                    // Fallback to basic employee data if fetch fails
                    const mappedEmployee = mapEmployeeFromApi(employee);
                    setFormData({
                        firstname: mappedEmployee.firstname || '',
                        lastname: mappedEmployee.lastname || '',
                        ssn: mappedEmployee.ssn || '',
                        email: mappedEmployee.email || '',
                        phonenumber: mappedEmployee.phonenumber || '',
                        jobtitle: mappedEmployee.jobtitle || '',
                        clearingnumber: mappedEmployee.clearingnumber || '',
                        accountnumber: mappedEmployee.accountnumber || '',
                        address: mappedEmployee.address || '',
                        postnummer: mappedEmployee.postnummer || '',
                        ort: mappedEmployee.ort || '',
                        active: mappedEmployee.active !== undefined ? mappedEmployee.active : true
                    });
                    setErrors({});
                    setShowValidation(false);
                }
            }
        };

        fetchFullEmployeeData();
    }, [show, employee, companyId]);

    // Validation rules
    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.firstname.trim()) {
            newErrors.firstname = 'Förnamn är obligatoriskt';
        }
        if (!formData.lastname.trim()) {
            newErrors.lastname = 'Efternamn är obligatoriskt';
        }
        if (!formData.ssn.trim()) {
            newErrors.ssn = 'Personnummer är obligatoriskt';
        }

        // Email validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Ogiltig e-postadress';
        }

        // Phone validation (simple Swedish format)
        if (formData.phonenumber && !/^[\d\s\-\+\(\)]{8,}$/.test(formData.phonenumber)) {
            newErrors.phonenumber = 'Ogiltigt telefonnummer';
        }

        // SSN validation using utility function
        const ssnError = getSSNValidationError(formData.ssn);
        if (ssnError) {
            newErrors.ssn = ssnError;
        }

        // Postal code validation
        if (formData.postnummer && !/^\d{3}\s?\d{2}$/.test(formData.postnummer)) {
            newErrors.postnummer = 'Postnummer måste vara i format XXX XX';
        }

        // Clearing number validation
        if (formData.clearingnumber) {
            const clearingResult = validateClearingNumber(formData.clearingnumber);
            if (!clearingResult.valid) {
                newErrors.clearingnumber = clearingResult.message;
            }
        }

        // Account number validation (only if clearing number is provided and valid)
        if (formData.accountnumber && formData.clearingnumber && !newErrors.clearingnumber) {
            const accountResult = validateAccountNumber(formData.clearingnumber, formData.accountnumber);
            if (!accountResult.valid) {
                newErrors.accountnumber = accountResult.message;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Identify bank when clearing number changes (only when complete)
        if (name === 'clearingnumber') {
            const cleanValue = newValue.replace(/\D/g, '');
            if (cleanValue.length >= 4 && cleanValue.length <= 5) {
                const bankInfo = getBankInfo(newValue);
                if (bankInfo) {
                    setIdentifiedBank(bankInfo.comment ? `${bankInfo.bank} (${bankInfo.comment})` : bankInfo.bank);
                } else {
                    setIdentifiedBank(null);
                }
            } else {
                setIdentifiedBank(null);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowValidation(true);

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const updateEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${employee.employee_id}`);

            // Transform form data to match backend field expectations
            const apiPayload = mapEmployeeToApi(formData);
            console.log('EmployeeEditModal: Sending API payload:', apiPayload);

            const response = await axios.put(updateEmployeeApiUrl, apiPayload, { withCredentials: true });
            console.log('EmployeeEditModal: Update response:', response.data);

            if (onSave) {
                onSave(formData);
            }
            handleClose();
        } catch (error) {
            console.error('Error updating employee:', error);
            const errorMessage = error.response?.data?.message || 'Det gick inte att uppdatera anställd. Försök igen.';
            setErrors({
                submit: errorMessage
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            firstname: '',
            lastname: '',
            ssn: '',
            email: '',
            phonenumber: '',
            jobtitle: '',
            clearingnumber: '',
            accountnumber: '',
            address: '',
            postnummer: '',
            ort: '',
            active: true
        });
        setErrors({});
        setShowValidation(false);
        handleClose();
    };

    // Memoized callback for toggling SSN visibility
    const handleToggleSSN = useCallback(() => {
        setShowSSN(prev => !prev);
    }, []);

    return (
        <>
            <style>{`
        .employee-modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .employee-modal-header {
          background: #f8fafc;
          color: #1e293b;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        
        .employee-modal-body {
          padding: 2rem;
          background: #ffffff;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .close-button {
          position: absolute;
          top: 1rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        
        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .form-section {
          margin-bottom: 2rem;
        }
        
        .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }
        
        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .input-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin: 0;
        }
        
        .required-indicator {
          color: #ef4444;
          margin-left: 0.25rem;
        }
        
        .input-description {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.4;
        }
        
        .input-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .input-icon {
          position: absolute;
          left: 0.75rem;
          z-index: 1;
          color: #6b7280;
          pointer-events: none;
        }
        
        .modern-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #374151;
          transition: all 0.2s ease;
        }

        .modern-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .modern-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        .input-toggle {
          position: absolute;
          right: 0.75rem;
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          z-index: 1;
          padding: 0.25rem;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        
        .input-toggle:hover {
          color: #374151;
          background: #f3f4f6;
        }
        
        .error-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .checkbox-group {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            margin-top: 1rem;
        }

        .checkbox-label {
            font-size: 0.875rem;
            font-weight: 500;
            color: #374151;
            cursor: pointer;
            margin: 0;
            display: flex;
            align-items: center;
        }

        /* Style the Bootstrap checkbox */
        .checkbox-group .form-check-input {
            width: 18px;
            height: 18px;
            border: 2px solid #d1d5db;
            border-radius: 4px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
            margin: 0;
        }

        .checkbox-group .form-check-input:checked {
            background-color: var(--bs-primary);
            border-color: var(--bs-primary);
        }

        .checkbox-group .form-check-input:focus {
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            border-color: var(--bs-primary);
        }

        
        .validation-summary {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .validation-icon {
          color: #ef4444;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .validation-content {
          flex: 1;
        }
        
        .validation-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #dc2626;
          margin-bottom: 0.5rem;
        }
        
        .validation-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .validation-item {
          font-size: 0.75rem;
          color: #dc2626;
          margin-bottom: 0.25rem;
        }
        
        .modal-actions {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .action-button {
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-cancel {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .btn-cancel:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .btn-save {
          background: var(--bs-primary);
          color: white;
        }
        
        .btn-save:hover {
          background: var(--bs-primary);
        }
        
        .btn-save:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }
        
        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-info {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .info-icon {
          color: #0284c7;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .info-text {
          font-size: 0.875rem;
          color: #0c4a6e;
          margin: 0;
        }
        
        /* Responsive design */
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-actions {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .action-button {
            justify-content: center;
          }
        }
      `}</style>

            <Modal show={show} onHide={handleCancel} size="lg" backdrop="static" centered>
                <div className="employee-modal-content">
                    {/* Header */}
                    <div className="employee-modal-header">
                        <button className="close-button" onClick={handleCancel}>
                            <X size={16} />
                        </button>
                        <div className="d-flex align-items-center gap-3">
                            <User size={24} color="#64748b" />
                            <div>
                                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    Redigera anställd
                                </h3>
                                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                                    Uppdatera information för {employee?.firstname} {employee?.lastname}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="employee-modal-body">
                        <Form onSubmit={handleSubmit}>
                            {/* Info message */}
                            <div className="form-info">
                                <CheckCircle size={16} className="info-icon" />
                                <p className="info-text">
                                    Obligatoriska fält är markerade med *. All information krypteras och hanteras säkert.
                                </p>
                            </div>

                            {/* Validation Summary */}
                            {showValidation && Object.keys(errors).length > 0 && (
                                <div className="validation-summary">
                                    <AlertTriangle size={16} className="validation-icon" />
                                    <div className="validation-content">
                                        <div className="validation-title">
                                            Åtgärda följande fel innan du fortsätter:
                                        </div>
                                        <ul className="validation-list">
                                            {Object.values(errors).filter(error => error).map((error, index) => (
                                                <li key={index} className="validation-item">• {error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Personal Information */}
                            <div className="form-section">
                                <div className="section-title">
                                    <User size={18} />
                                    Personuppgifter
                                </div>

                                <div className="form-grid">
                                    <InputField
                                        icon={User}
                                        label="Förnamn"
                                        name="firstname"
                                        required={true}
                                        placeholder="Ange förnamn"
                                        value={formData.firstname}
                                        onChange={handleInputChange}
                                        error={errors.firstname}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={User}
                                        label="Efternamn"
                                        name="lastname"
                                        required={true}
                                        placeholder="Ange efternamn"
                                        value={formData.lastname}
                                        onChange={handleInputChange}
                                        error={errors.lastname}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={CreditCard}
                                        label="Personnummer (ÅÅMMDD-XXXX)"
                                        name="ssn"
                                        required={true}
                                        placeholder="ÅÅMMDD-XXXX"
                                        showToggle={true}
                                        value={formData.ssn}
                                        onChange={handleInputChange}
                                        error={errors.ssn}
                                        showValidation={showValidation}
                                        showSSN={showSSN}
                                        onToggleSSN={handleToggleSSN}
                                    />

                                    <InputField
                                        icon={Building2}
                                        label="Jobbtitel"
                                        name="jobtitle"
                                        placeholder="Ange jobbtitel"
                                        value={formData.jobtitle}
                                        onChange={handleInputChange}
                                        error={errors.jobtitle}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="form-section">
                                <div className="section-title">
                                    <Mail size={18} />
                                    Kontaktuppgifter
                                </div>

                                <div className="form-grid">
                                    <InputField
                                        icon={Mail}
                                        label="E-postadress"
                                        name="email"
                                        type="email"
                                        placeholder="namn@exempel.se"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={errors.email}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={Phone}
                                        label="Telefonnummer"
                                        name="phonenumber"
                                        placeholder="+46 XX XXX XX XX"
                                        value={formData.phonenumber}
                                        onChange={handleInputChange}
                                        error={errors.phonenumber}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>

                            {/* Address Information */}
                            <div className="form-section">
                                <div className="section-title">
                                    <MapPin size={18} />
                                    Adressuppgifter
                                </div>

                                <div className="form-grid">
                                    <InputField
                                        icon={MapPin}
                                        label="Adress"
                                        name="address"
                                        placeholder="Gatunamn XX"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        error={errors.address}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={MapPin}
                                        label="Postnummer"
                                        name="postnummer"
                                        placeholder="XXX XX"
                                        value={formData.postnummer}
                                        onChange={handleInputChange}
                                        error={errors.postnummer}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={MapPin}
                                        label="Ort"
                                        name="ort"
                                        placeholder="Ortnamn"
                                        value={formData.ort}
                                        onChange={handleInputChange}
                                        error={errors.ort}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>

                            {/* Bank Information 
                            <div className="form-section">
                                <div className="section-title">
                                    <CreditCard size={18} />
                                    Bankuppgifter
                                </div>

                                <div className="form-grid">
                                    <div>
                                        <InputField
                                            icon={CreditCard}
                                            label="Clearingnummer"
                                            name="clearingnumber"
                                            placeholder="XXXX"
                                            description="4-5 siffror"
                                            value={formData.clearingnumber}
                                            onChange={handleInputChange}
                                            error={errors.clearingnumber}
                                            showValidation={showValidation}
                                        />
                                        {identifiedBank && !errors.clearingnumber && formData.clearingnumber && (
                                            <div style={{
                                                marginTop: '4px',
                                                fontSize: '0.75rem',
                                                color: '#10b981',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                paddingLeft: '36px'
                                            }}>
                                                <CheckCircle size={12} />
                                                <span>{identifiedBank}</span>
                                            </div>
                                        )}
                                    </div>

                                    <InputField
                                        icon={CreditCard}
                                        label="Kontonummer"
                                        name="accountnumber"
                                        placeholder="XXXXXXXXX"
                                        description="Kontonummer utan bindestreck"
                                        value={formData.accountnumber}
                                        onChange={handleInputChange}
                                        error={errors.accountnumber}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>
                            */}

                            {/* Status */}
                            <div className="checkbox-group">
                                <Form.Check
                                    type="checkbox"
                                    id="active-checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleInputChange}
                                />
                                <label className="checkbox-label" htmlFor="active-checkbox">
                                    Aktiv anställd
                                </label>
                            </div>
                        </Form>
                    </div>

                    {/* Actions */}
                    <div className="modal-actions">
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                            * Obligatoriska fält
                        </div>
                        <div className="d-flex gap-3">
                            <button
                                className="action-button btn-cancel"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Avbryt
                            </button>
                            <button
                                className="action-button btn-save"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="loading-spinner"></div>
                                        Sparar...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Spara ändringar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EmployeeEditModal;